import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

import { login } from "../slices/auth";
import { clearMessage } from "../slices/message";
import FEATURES from "../config/features";

const Login = () => {
  let navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  // A4 – Zustand des zweiten Faktors.
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  const { isLoggedIn } = useSelector((state) => state.auth);
  const { message } = useSelector((state) => state.message);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const initialValues = {
    username: "",
    password: "",
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("Bitte geben Sie Ihren Benutzernamen ein."),
    password: Yup.string().required("Bitte geben Sie Ihr Passwort ein."),
  });

  const proceedAfterLogin = (data) => {
    if (data && data.mustSetup2FA) {
      // Admin mit Schonfrist: sofort zur 2FA-Einrichtung.
      navigate("/2fa-einrichten");
      return;
    }
    navigate("/customerinfo");
    window.location.reload();
  };

  const handleLogin = (formValue) => {
    const { username, password } = formValue;
    setCreds({ username, password });
    setLoading(true);

    dispatch(login({ username, password }))
      .unwrap()
      .then((payload) => {
        const data = payload && payload.user;
        if (data && data.twoFactorRequired) {
          setTwoFactorRequired(true);
          setLoading(false);
          return;
        }
        proceedAfterLogin(data);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleTwoFactorSubmit = (event) => {
    event.preventDefault();
    const clean = code.replace(/\s+/g, "");
    if (!clean) return;
    setLoading(true);
    dispatch(clearMessage());
    dispatch(
      login({
        username: creds.username,
        password: creds.password,
        totp: useBackup ? undefined : clean,
        backupCode: useBackup ? clean : undefined,
      })
    )
      .unwrap()
      .then((payload) => {
        const data = payload && payload.user;
        if (data && data.accessToken) {
          proceedAfterLogin(data);
          return;
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  if (isLoggedIn) {
    // Folgeprompt 15: Alle Stufen landen nach Login auf dem Dashboard.
    return <Navigate to="/customerinfo" />;
  }

  return (
    <div className="myn-auth">
      <div className="myn-card myn-auth-card">
        <div className="myn-auth__head">
          <img src="/myn-logo-full.svg" alt="MYN.CLUB" />
          <h1>Willkommen zurück</h1>
          <p>Melden Sie sich an, um Ihre Produkte und Ihren Vertrieb zu verwalten.</p>
        </div>

        {!twoFactorRequired ? (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            <Form>
              <div className="myn-field">
                <label className="myn-label" htmlFor="username">Benutzername</label>
                <Field name="username" type="text" className="myn-input" autoComplete="username" />
                <ErrorMessage name="username" component="div" className="myn-error" />
              </div>

              <div className="myn-field">
                <label className="myn-label" htmlFor="password">Passwort</label>
                <Field name="password" type="password" className="myn-input" autoComplete="current-password" />
                <ErrorMessage name="password" component="div" className="myn-error" />
              </div>

              <button type="submit" className="myn-btn myn-btn--primary myn-btn--block" disabled={loading}>
                {loading && <span className="spinner-border spinner-border-sm" />}
                <span>Anmelden</span>
              </button>

              {FEATURES.passwordReset && (
                <p className="myn-auth__alt">
                  <Link to="/passwort-vergessen">Passwort vergessen?</Link>
                </p>
              )}
            </Form>
          </Formik>
        ) : (
          <form onSubmit={handleTwoFactorSubmit}>
            <div className="myn-field">
              <label className="myn-label" htmlFor="totp">
                {useBackup ? "Backup-Code" : "Code aus Ihrer Authenticator-App"}
              </label>
              <input
                id="totp"
                type="text"
                inputMode={useBackup ? "text" : "numeric"}
                autoComplete="one-time-code"
                className="myn-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={useBackup ? "XXXX-XXXX" : "6-stelliger Code"}
                autoFocus
              />
            </div>

            <button type="submit" className="myn-btn myn-btn--primary myn-btn--block" disabled={loading}>
              {loading && <span className="spinner-border spinner-border-sm" />}
              <span>Bestätigen</span>
            </button>

            <button
              type="button"
              className="myn-btn myn-btn--link myn-btn--block"
              onClick={() => {
                setUseBackup((v) => !v);
                setCode("");
                dispatch(clearMessage());
              }}
            >
              {useBackup ? "Stattdessen App-Code verwenden" : "Backup-Code verwenden"}
            </button>
          </form>
        )}

        {message && (
          <div className="myn-alert myn-alert--danger" role="alert">
            {message}
          </div>
        )}

        <p className="myn-auth__alt">
          Noch kein Konto? <Link to="/register">Jetzt kostenlos registrieren</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;